import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { TenantService } from '../services/tenant.service';
import { UserManagementService } from '../../../user-management/services/user-management.service';
import { UserDetail, UserOrganization } from '../../../user-management/models/user.model';
import { BreadcrumbService } from '../../../../shared/components/breadcrumbs/breadcrumb.service';
import { ToastService } from '../../../../shared/services/toast-service';
import { LabelBadge } from '../../../../shared/components/label-badge/label-badge';

/**
 * Read-only user view (/admin/tenants/:tenantId/users/:userId) — super-admin
 * only. The deepest level of the tenants drill-down; navigation back up goes
 * through the breadcrumb trail.
 */
@Component({
  selector: 'app-admin-user-detail',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, LabelBadge],
  templateUrl: './user-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUserDetailComponent implements OnInit {
  private readonly userService = inject(UserManagementService);
  private readonly tenantService = inject(TenantService);
  private readonly breadcrumbs = inject(BreadcrumbService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly user = signal<UserDetail | null>(null);
  readonly isLoading = signal(true);

  // EmailAsUsername realm: title is the real name, falling back to the email.
  readonly displayName = computed(() => {
    const u = this.user();
    if (!u) return '';
    const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
    return fullName || u.email;
  });

  /** Hide the email subline when it would just repeat the title. */
  readonly showEmailSubline = computed(() => this.user()?.email !== this.displayName());

  /** Cross-link to the organization's tenant page, naming its crumb first. */
  openOrganization(org: UserOrganization): void {
    this.breadcrumbs.setLabel('tenantId', org.id, org.name);
    void this.router.navigate(['/admin', 'tenants', org.id]);
  }

  ngOnInit(): void {
    const userId = this.route.snapshot.paramMap.get('userId') ?? '';
    const tenantId = this.route.snapshot.paramMap.get('tenantId') ?? '';

    this.userService
      .getUserById(userId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.user.set(user);
          this.breadcrumbs.setLabel('userId', userId, user.email);
          this.isLoading.set(false);
        },
        error: () => {
          this.toastService.show(this.transloco.translate('admin.userDetail.toast.loadFailed'), 'error');
          this.isLoading.set(false);
        },
      });

    // Only needed on direct/refreshed loads — coming through the drill-down,
    // the tenant crumb is already named. The registry call is idempotent.
    this.tenantService
      .getTenant(tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tenant) => this.breadcrumbs.setLabel('tenantId', tenant.id, tenant.name));
  }
}
